import type { DispatchRecipient } from "@/utils/dispatchControl";
import type { PremiumCorrection } from "@/types/simpego-api";
"use client";

import { SerializedFunnelData } from "@/hooks/funnelPersistence";
import type { OfferDispatchSnapshot } from "@/utils/dispatchControl";
import { getEarliestStartOfInsurance, createProductVariant, ProductVariant } from "@/utils/funnel";
import { BaseCoverageId } from "@/config/coverages";
import { ApplicationAnswer, ApplicationAnswers, EMPTY_APPLICATION_ANSWERS } from "@/components/contact-details/ApplicationQuestionsSection";
import { EMPTY_COVERAGE_STATE } from "@/utils/coveragePayload";

const toApplicationAnswer = (value: string | null | undefined): ApplicationAnswer => (value === "yes" || value === "no" ? value : "");

export interface HydratedValues {
  contractId: string | null;
  contractToken: string | null;
  offerAgentId: string | null;
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  breedLabel: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  chipNumber: string | null;
  preexistingConditionCategories: string[];
  preexistingConditionComment: string | null;
  bundlePrices: { S: number; M: number; L: number } | null;
  selectedProduct: ProductVariant | null;
  finalPrice: number | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  street: string | null;
  housenumber: string | null;
  city: string | null;
  zipCode: string | null;
  country: string | null;
  contactBirthdate: Date | undefined;
  customerGender: "male" | "female" | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  coverageDeductiblesOtherBand: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  annualDeductible: number | null;
  dailyTermination: boolean;
  hasAppliedRecommendation: boolean;
  /** Whether the customer has ever landed on the coverage customization page this session —
   * used so "Zurück" from contact details can skip straight to the product variant table when
   * they took the "Auswählen" fast path and never actually saw customization. */
  hasSeenProductCustomization: boolean;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  isOfferLoadingFromUrl: boolean;
  isHydrated: boolean;
  dispatchContract: DispatchRecipient;
  dispatchInvoice: DispatchRecipient;
  dispatchReminder: DispatchRecipient;
  dispatchEmail: DispatchRecipient;
  agentDispatchEmail: string | null;
  campaignCode: string;
  applicationAnswers: ApplicationAnswers;
  agentId: string | null;
  offerWhitelabelPartner: string | null;
  offerDispatchSnapshot: OfferDispatchSnapshot | null;
  premiumCorrections: PremiumCorrection[] | null;
  offerLoadedFromUrl: boolean;
  hasLeftConfirmationAfterOfferLoad: boolean;
  consultationProtocolDocumentId: number | null;
  consultationProtocolFilename: string | null;
  consultationProtocolPendingFile: string | null;
  consultationProtocolPendingFilename: string | null;
}

export const getDefaultValues = (options?: { defaultDispatchEmail?: DispatchRecipient }): HydratedValues => {
  const defaultDispatchEmail = options?.defaultDispatchEmail ?? "customer";
  return {
    contractId: null,
    contractToken: null,
    offerAgentId: null,
    petType: null,
    petName: null,
    breed: null,
    breedLabel: null,
    birthDate: undefined,
    selectedBreedType: null,
    gender: null,
    isCastrated: false,
    isHouseCat: false,
    selectedDogSize: null,
    chipNumber: null,
    preexistingConditionCategories: [],
    preexistingConditionComment: null,
    bundlePrices: null,
    selectedProduct: null,
    finalPrice: null,
    contactFirstName: null,
    contactLastName: null,
    contactEmail: null,
    contactPhone: null,
    street: null,
    housenumber: null,
    city: null,
    zipCode: null,
    country: null,
    contactBirthdate: undefined,
    customerGender: null,
    // Hardcoded recommendation: Krankheit and Unfall are preselected, the rest is up to the customer.
    coverageSelections: { disease: true, accident: true },
    subCoverageSelections: EMPTY_COVERAGE_STATE.subCoverageSelections,
    subCoverageSumInsured: EMPTY_COVERAGE_STATE.subCoverageSumInsured,
    coverageDeductibles: EMPTY_COVERAGE_STATE.coverageDeductibles,
    coverageDeductiblesOtherBand: EMPTY_COVERAGE_STATE.coverageDeductiblesOtherBand ?? {},
    coverageSumInsured: EMPTY_COVERAGE_STATE.coverageSumInsured,
    coverageSumInsuredPerCase: EMPTY_COVERAGE_STATE.coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay: EMPTY_COVERAGE_STATE.emergencyCareSumInsuredPerDay,
    annualDeductible: EMPTY_COVERAGE_STATE.annualDeductible,
    dailyTermination: false,
    hasAppliedRecommendation: false,
    hasSeenProductCustomization: false,
    paymentInterval: 12,
    paymentMethod: "credit_card",
    startOfInsurance: getEarliestStartOfInsurance(),
    firstDue: null,
    isOfferLoadingFromUrl: false,
    isHydrated: false,
    dispatchContract: "customer",
    dispatchInvoice: "customer",
    dispatchReminder: "customer",
    dispatchEmail: defaultDispatchEmail,
    agentDispatchEmail: null,
    campaignCode: "",
    applicationAnswers: EMPTY_APPLICATION_ANSWERS,
    agentId: null,
    offerWhitelabelPartner: null,
    offerDispatchSnapshot: null,
    premiumCorrections: null,
    offerLoadedFromUrl: false,
    hasLeftConfirmationAfterOfferLoad: false,
    consultationProtocolDocumentId: null,
    consultationProtocolFilename: null,
    consultationProtocolPendingFile: null,
    consultationProtocolPendingFilename: null,
  };
};

export const hydrateValuesFromStorage = (
  data: SerializedFunnelData,
  defaultDispatchEmail: DispatchRecipient = "customer"
): Omit<HydratedValues, "agentId"> => {
  const defaults = getDefaultValues({ defaultDispatchEmail });
  const parseOptionalDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  // A persisted start of insurance may have fallen below the earliest-start floor in the
  // meantime (it is now today/in the past, or predates EARLIEST_START_OF_INSURANCE) — lift it
  // to the floor instead of restoring an invalid date.
  const restoreStartOfInsurance = (value: string | null | undefined, fallback: Date): Date => {
    const parsed = parseOptionalDate(value);
    if (!parsed) return fallback;
    return parsed < fallback ? fallback : parsed;
  };

  const selectedProduct =
    data.selectedProductId ? createProductVariant(data.selectedProductId as "s" | "m" | "l") : null;

  // Handle backward compatibility for contactName
  let hydratedFirstName = data.contactFirstName ?? defaults.contactFirstName;
  let hydratedLastName = data.contactLastName ?? defaults.contactLastName;

  if (data.contactName && !data.contactFirstName && !data.contactLastName) {
    const parts = data.contactName.trim().split(/\s+/);
    hydratedFirstName = parts[0] || null;
    hydratedLastName = parts.slice(1).join(" ") || null;
  }

  return {
    contractId: data.contractId ?? defaults.contractId,
    contractToken: data.contractToken ?? defaults.contractToken,
    offerAgentId: data.offerAgentId ?? defaults.offerAgentId,
    offerWhitelabelPartner: data.offerWhitelabelPartner ?? defaults.offerWhitelabelPartner,
    petType: data.petType ?? defaults.petType,
    petName: data.petName ?? defaults.petName,
    breed: data.breed ?? defaults.breed,
    breedLabel: data.breedLabel ?? defaults.breedLabel,
    birthDate: data.birthDate ? new Date(data.birthDate) : defaults.birthDate,
    selectedBreedType: data.selectedBreedType ?? defaults.selectedBreedType,
    gender: data.gender ?? defaults.gender,
    isCastrated: !!data.isCastrated,
    isHouseCat: !!data.isHouseCat,
    selectedDogSize: data.selectedDogSize ?? defaults.selectedDogSize,
    chipNumber: data.chipNumber ?? defaults.chipNumber,
    preexistingConditionCategories: Array.isArray(data.preexistingConditionCategories) ? data.preexistingConditionCategories : defaults.preexistingConditionCategories,
    preexistingConditionComment: data.preexistingConditionComment ?? defaults.preexistingConditionComment,
    bundlePrices: data.bundlePrices ?? defaults.bundlePrices,
    selectedProduct,
    finalPrice: data.finalPrice ?? defaults.finalPrice,
    contactFirstName: hydratedFirstName,
    contactLastName: hydratedLastName,
    contactEmail: data.contactEmail ?? defaults.contactEmail,
    contactPhone: data.contactPhone ?? defaults.contactPhone,
    street: data.street ?? defaults.street,
    housenumber: data.housenumber ?? defaults.housenumber,
    city: data.city ?? defaults.city,
    zipCode: data.zipCode ?? defaults.zipCode,
    country: data.country ?? defaults.country,
    contactBirthdate: data.contactBirthdate ? new Date(data.contactBirthdate) : defaults.contactBirthdate,
    customerGender: data.customerGender ?? defaults.customerGender,
    coverageSelections: data.coverageSelections ?? defaults.coverageSelections,
    subCoverageSelections: data.subCoverageSelections ?? defaults.subCoverageSelections,
    subCoverageSumInsured: data.subCoverageSumInsured ?? defaults.subCoverageSumInsured,
    coverageDeductibles: data.coverageDeductibles ?? defaults.coverageDeductibles,
    coverageDeductiblesOtherBand: data.coverageDeductiblesOtherBand ?? defaults.coverageDeductiblesOtherBand,
    coverageSumInsured: data.coverageSumInsured ?? defaults.coverageSumInsured,
    coverageSumInsuredPerCase: data.coverageSumInsuredPerCase ?? defaults.coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay: data.emergencyCareSumInsuredPerDay ?? defaults.emergencyCareSumInsuredPerDay,
    annualDeductible: data.annualDeductible ?? defaults.annualDeductible,
    dailyTermination: !!data.dailyTermination,
    hasAppliedRecommendation: !!data.hasAppliedRecommendation,
    hasSeenProductCustomization: !!data.hasSeenProductCustomization,
    paymentInterval: data.paymentInterval ?? defaults.paymentInterval,
    paymentMethod: data.paymentMethod ?? defaults.paymentMethod,
    startOfInsurance: restoreStartOfInsurance(data.startOfInsurance, defaults.startOfInsurance),
    firstDue: parseOptionalDate(data.firstDue) ?? defaults.firstDue,
    isOfferLoadingFromUrl: !!data.isOfferLoadingFromUrl,
    isHydrated: true, // Always set to true after hydration attempt
    dispatchContract: data.dispatchContract ?? defaults.dispatchContract,
    dispatchInvoice: data.dispatchInvoice ?? defaults.dispatchInvoice,
    dispatchReminder: data.dispatchReminder ?? defaults.dispatchReminder,
    dispatchEmail: data.dispatchEmail ?? defaults.dispatchEmail,
    agentDispatchEmail: data.agentDispatchEmail ?? defaults.agentDispatchEmail,
    campaignCode: data.campaignCode ?? defaults.campaignCode,
    applicationAnswers: {
      termination: toApplicationAnswer(data.applicationAnswers?.termination),
      owner: toApplicationAnswer(data.applicationAnswers?.owner),
    },
    offerDispatchSnapshot: data.offerDispatchSnapshot ?? defaults.offerDispatchSnapshot,
    premiumCorrections: data.premiumCorrections ?? defaults.premiumCorrections,
    offerLoadedFromUrl: !!data.offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad: !!data.hasLeftConfirmationAfterOfferLoad,
    consultationProtocolDocumentId: data.consultationProtocolDocumentId ?? defaults.consultationProtocolDocumentId,
    consultationProtocolFilename: data.consultationProtocolFilename ?? defaults.consultationProtocolFilename,
    // Never restored: older versions persisted the Base64 file, which must not be revived (see useFunnelState).
    consultationProtocolPendingFile: defaults.consultationProtocolPendingFile,
    consultationProtocolPendingFilename: defaults.consultationProtocolPendingFilename,
  };
};
